import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect } from 'react';

import { supabase } from './supabase';

/**
 * Hook que assina mudancas em UMA tabela do Postgres via Supabase Realtime
 * e inválida queries do TanStack Query quando algo muda — assim a UI
 * atualiza sozinha, sem o usuário precisar dar refresh.
 *
 * Pre-requisito: a tabela precisa estar na publicacao supabase_realtime
 * (ver migration `20260513090000_realtime_publication.sql`).
 *
 * Uso:
 *   useRealtimeInvalidate('tab_lavagens', ['cliente-lavagens', userId]);
 *
 * Eventos cobertos: INSERT + UPDATE + DELETE.
 *
 * Performance: cria UM channel por table+queryKey. Multiplos componentes
 * que escutam a mesma tabela criam multiplos channels — Supabase aguenta
 * mas se virar bottleneck, refator pra um channel global compartilhado.
 */
export function useRealtimeInvalidate(table: string, queryKey: QueryKey) {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Tolerante a ambientes (testes vitest, SSR) onde o supabase mockado
    // não implementa .channel/.removeChannel. Sem isso, qualquer pagina
    // que use este hook explode em test com "channel is not a function".
    if (typeof supabase.channel !== 'function') return;

    // queryKey é array — serializa pra ID estavel do channel
    const channelId = `rt-${table}-${JSON.stringify(queryKey)}`;
    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', { event: '*', schema: 'public', table }, () => {
        void queryClient.invalidateQueries({ queryKey });
      })
      .subscribe();

    return () => {
      if (typeof supabase.removeChannel === 'function') {
        void supabase.removeChannel(channel);
      }
    };
    // queryKey serializado para evitar re-subscribe a cada render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, JSON.stringify(queryKey)]);
}

/**
 * Versão multi-tabela. Útil quando uma tela junta dados de várias tabelas
 * (ex: lavagens + maquinas + clientes).
 *
 * Cria 1 channel por tabela. Todos invalidam o mesmo queryKey.
 */
export function useRealtimeInvalidateMulti(tables: string[], queryKey: QueryKey) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof supabase.channel !== 'function') return;
    const channels = tables.map((table) => {
      const channelId = `rt-${table}-${JSON.stringify(queryKey)}`;
      return supabase
        .channel(channelId)
        .on('postgres_changes', { event: '*', schema: 'public', table }, () => {
          void queryClient.invalidateQueries({ queryKey });
        })
        .subscribe();
    });

    return () => {
      if (typeof supabase.removeChannel !== 'function') return;
      for (const ch of channels) {
        void supabase.removeChannel(ch);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tables), JSON.stringify(queryKey)]);
}
